import DoDisturbAltRoundedIcon from '@mui/icons-material/DoDisturbAltRounded';
import HearingDisabledRoundedIcon from '@mui/icons-material/HearingDisabledRounded';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded';
import { theme } from '@ui/styles';
import { MinimapConfig, MinimapGradientStop } from './range-brush/minimap-config';

/**
 * MUI SvgIcon components reused by stream-quality KPI cards, event chips, and related UI.
 * Map semantic roles (health, incidents, continuity/gap, audio quality) to icons.
 */
export const QUALITY_ICONS = {
  healthScore: MonitorHeartOutlinedIcon,
  stationsWithIncidents: ReportGmailerrorredRoundedIcon,
  gap: DoDisturbAltRoundedIcon,
  quality: HearingDisabledRoundedIcon,
  /** Stream segment: silent audio (`AudioQualityIssueDto.kind === silent`). */
  audioSilent: HearingDisabledRoundedIcon,
  /** Stream segment: poor audio quality (`AudioQualityIssueDto.kind === poor_quality`). */
  audioPoorQuality: HearingDisabledRoundedIcon,
} as const;

/**
 * Hex accents for stream-quality status segments (timeline bars, minimap gradient, chips).
 * Fixed values so visx/SVG fills do not depend on the MUI theme object.
 */
export const QUALITY_STATUS_COLORS = {
  /** Healthy / on-air interval (timeline baseline). */
  // online: '#00c853',
  online: theme.green,
  /** Stream gap / no emission (timeline). */
  // offline: '#ff1744',
  offline: theme.pink,
  /** Audio issue: `AudioQualityIssueDto.kind === poor_quality`. */
  // issuePoorQuality: '#fdd835',
  issuePoorQuality: theme.yellow,
  /** Audio issue: `AudioQualityIssueDto.kind === silent`. */
  // issueSilent: '#ff6d00',
  issueSilent: theme.orange,
} as const;

const QUALITY_TIMELINE_MARGIN = {
  top: 40,
  right: 16,
  bottom: 16,
  left: 210,
} as const;

/**
 * Top timeline axis: fewer ticks when zoomed far out, and `visibleHours` thresholds for tick label granularity (see `formatTimelineAxisTick`).
 */
export const QUALITY_TIMELINE_AXIS = {
  /** When the visible window spans more than this many hours, the top axis uses fewer ticks. */
  fewTicksThresholdHours: 72,
  numTicksWhenWide: 5,
  numTicksDefault: 10,
  /** `visibleHours` breakpoints for timeline tick label granularity. */
  tickVisibleHoursDayFocus: 48,
  tickVisibleHoursDateTime: 20,
  tickVisibleHoursHmOnly: 1,
} as const;

/**
 * Timeline chart layout, axis behaviour, and fixed SVG/chart colors (grid, ticks, minimap neutral).
 * Shared with the overview range brush so X scales and left gutter stay aligned (`margin`).
 */
export const QUALITY_TIMELINE = {
  chartColors: {
    /** Grid line stroke (aligned with a light divider tone). */
    grid: '#e0e0e0',
    /** Axis tick label fill (~50% black on white, matches app `text.secondary` intent). */
    axisTick: '#808080',
    /** Minimap background when there are no bins (~light `action.hover`). */
    minimapNeutral: '#f5f5f5',
  },
  margin: QUALITY_TIMELINE_MARGIN,
  /**
   * `foreignObject` width for timeline station tick labels; derived from `margin.left` so both stay in sync.
   * A few pixels short of `left` avoids clipping past the SVG edge relative to visx tick `anchorX`.
   */
  stationTickLabelWidth: QUALITY_TIMELINE_MARGIN.left - 12,
  /** Pixel height of each station row in the main timeline chart. */
  rowHeight: 45,
  /**
   * Vertical inset (px) above and below the painted timeline bars within each row.
   * Rows stay contiguous (`scaleBand` padding 0); this replaces gap-between-rows band padding.
   */
  barVerticalInset: 5,
} as const;

/**
 * Range brush layout, grip dimensions, selection clamp, and SVG colors (fixed hex; no MUI theme).
 */
export const QUALITY_BRUSH = {
  /** Inner height of the overview range brush SVG (minimap + brush). */
  innerHeight: 46,
  /** Visx `Brush` handle size (px); `gripPathWidth` matches this for aligned grips. */
  handleSize: 8,
  gripPathWidth: 8,
  gripPathHeight: 15,
  /** Default minimum selection width (ms), enforced by `clampVisibleTimeRange`. */
  defaultMinSelectionMs: 2 * 60 * 1000,
  /** Selected range fill/stroke (~`palette.primary.main`). */
  selectionColor: '#777777',
  handleFill: '#eeeeee',
  handleStroke: '#9e9e9e',
} as const;

/**
 * Success-rate breakpoints for the default quality gradient (offline → poor-quality tone → online).
 * Used by the minimap, `qualityReportColor`, and any other UI aligned with this ramp.
 */
export const DEFAULT_QUALITY_MINIMAP_GRADIENT_RATES = {
  error: 0.8,
  warning: 0.9,
  success: 1,
} as const;

/** Stops for the default quality color ramp (see `qualityReportColor`). */
export const DEFAULT_QUALITY_MINIMAP_GRADIENT_STOPS: MinimapGradientStop[] = [
  { rate: DEFAULT_QUALITY_MINIMAP_GRADIENT_RATES.error, color: QUALITY_STATUS_COLORS.offline },
  {
    rate: DEFAULT_QUALITY_MINIMAP_GRADIENT_RATES.warning,
    color: QUALITY_STATUS_COLORS.issuePoorQuality,
  },
  { rate: DEFAULT_QUALITY_MINIMAP_GRADIENT_RATES.success, color: QUALITY_STATUS_COLORS.online },
];

/**
 * Global defaults for the brush minimap. `fillOpacityRange` and `isStationHealthyInBin` can be
 * overridden via `minimapConfig` on `RangeBrush` (shallow merge: defaults first, then overrides).
 * Minimap time resolution is fixed at `binCount` here only.
 */
export const DEFAULT_QUALITY_MINIMAP_CONFIG: MinimapConfig & { binCount: number } = {
  binCount: 200,
  fillOpacityRange: { min: 0.4, max: 0.4 },
};

export const QUALITY_KPI_INCIDENT_GRADIENT_STOPS: MinimapGradientStop[] = [
  { rate: 0.925, color: QUALITY_STATUS_COLORS.offline },
  {
    rate: 0.9999,
    color: QUALITY_STATUS_COLORS.issuePoorQuality,
  },
  { rate: 1, color: QUALITY_STATUS_COLORS.online },
];
